// SectorHeatMap — Story #6837 Stream 11b (figure 5 = screen-M5-investor-sector-view.png).
//
// The aggregator's Portfolio > Sector view: one row per primary sector across every
// invention the fund may see, from GET /market/sectors?scope=portfolio (the API reads
// the aggregator from the verified token). Columns: inventions, average assessment,
// sector value for Australia and the world, and the summed SOM midpoint. The last two
// of those come from AI analysis, so the footer separates fact columns (official
// statistics, dated) from AI columns.
//
// Figure 5 also shows Filing momentum and PESTLE outlook columns, a Country / Stage
// filter, a "Where the modelled value sits" bar chart and a "Sectors to watch" panel.
// None has a data source in the portfolio payload (no per-sector patent-filing series,
// no portfolio PESTLE, no stage field, and the SOM figures are in mixed currencies that
// one bar axis cannot compare), so they are not drawn rather than invented.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { getMarketTaxonomy, getPortfolioSectors } from '../../services/api'
import { sectorColor } from '../../lib/sectorColors'
import { dateLabel, statValue } from './marketFormat'
import {
  assessmentStep, buildHeatModel, scaleStep, somText, sortRows,
} from './sectorHeatMapLogic'
import type { HeatRow, SortKey } from './sectorHeatMapLogic'
import type { MarketPortfolioReferenceValue } from '../../types/market'

const CARD = 'bg-white/70 border border-[rgba(12,74,110,0.12)] rounded-xl p-4'
const SOURCE_NAME: Record<string, string> = { abs: 'ABS', worldbank: 'World Bank', oecd: 'OECD' }

// Five steps each, light to strong. Text stays the dark brand blue except on the darkest blue.
const INVENTION_FILL = ['#E0F2FE', '#BAE6FD', '#7DD3FC', '#38BDF8', '#0369A1']
const ASSESSMENT_FILL = ['#FECACA', '#FED7AA', '#FEF08A', '#BBF7D0', '#6EE7B7']
const TEXT = '#0C4A6E'

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'assessment', label: 'Average assessment' },
  { key: 'inventions', label: 'Inventions' },
  { key: 'sector', label: 'Sector' },
]

function refText(r: MarketPortfolioReferenceValue | null): string {
  return r ? `${statValue(r)} (${r.year})` : '—'
}

function Swatches({ fills, low, high }: { fills: string[]; low: string; high: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
      {low}
      {fills.map((f) => <span key={f} className="inline-block h-3 w-5 rounded-sm border border-[rgba(12,74,110,0.12)]" style={{ background: f }} aria-hidden />)}
      {high}
    </span>
  )
}

/**
 * `aggregatorId` is the aggregator the reader is acting as. The API scopes the answer by it (the token's active
 * aggregator header), so it is part of the cache key: switching aggregator must never show the other one's portfolio.
 */
export default function SectorHeatMap({ aggregatorId }: { aggregatorId: string | null }) {
  const portfolio = useQuery({
    queryKey: ['market-sectors', 'portfolio', aggregatorId],
    queryFn: getPortfolioSectors,
    enabled: !!aggregatorId,
    staleTime: 60_000,
    retry: false,
  })
  const taxonomy = useQuery({ queryKey: ['market-taxonomy'], queryFn: getMarketTaxonomy, staleTime: Infinity, retry: false })
  const [sort, setSort] = useState<SortKey>('assessment')

  const model = useMemo(
    () => (portfolio.data ? buildHeatModel(portfolio.data, taxonomy.data) : null),
    [portfolio.data, taxonomy.data],
  )
  const rows: HeatRow[] = useMemo(() => (model ? sortRows(model.rows, sort) : []), [model, sort])

  if (portfolio.isLoading) {
    return <div className={CARD} data-testid="sector-heatmap-loading" role="status">Reading the portfolio’s sectors…</div>
  }
  // A failed refresh keeps the data already on screen; the error card is for "nothing to show".
  if (!model) {
    return (
      <div className={`${CARD} flex items-start gap-3`} data-testid="sector-heatmap-error" role="alert">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" aria-hidden />
        <div className="text-sm text-gray-700">
          The sector view could not be loaded. Nothing has been changed.
          <button type="button" onClick={() => void portfolio.refetch()} className="ml-2 font-semibold text-iflow-700 underline">Try again</button>
        </div>
      </div>
    )
  }

  const inventionCounts = rows.map((r) => r.inventions)
  const minInv = Math.min(...inventionCounts)
  const maxInv = Math.max(...inventionCounts)
  const refs = rows.flatMap((r) => [r.au, r.world]).filter((r): r is MarketPortfolioReferenceValue => r !== null)
  const sources = [...new Set(refs.map((r) => SOURCE_NAME[r.source] ?? r.source))].sort()
  const years = refs.map((r) => r.year)
  const asOfs = refs.map((r) => r.as_of).sort()
  const refreshed = dateLabel(asOfs[asOfs.length - 1])
  const somInventions = rows.reduce((n, r) => n + r.somCount, 0)
  const aiFrom = model.aiDates ? dateLabel(model.aiDates.from) : null
  const aiTo = model.aiDates ? dateLabel(model.aiDates.to) : null
  const aiWhen = aiFrom && aiTo ? (aiFrom === aiTo ? `as at ${aiTo}` : `run between ${aiFrom} and ${aiTo}`) : null
  const classified = model.total - model.unclassified

  return (
    <section className={CARD} data-testid="sector-heatmap" aria-label="Sector heat map">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="text-base font-bold text-iflow-900">
            Sector heat map
            <span className="ml-2 rounded-full bg-iflow-50 px-2 py-0.5 text-[11px] font-semibold text-iflow-700" data-testid="sector-heatmap-count">
              {classified} {classified === 1 ? 'invention' : 'inventions'} · {rows.length} {rows.length === 1 ? 'sector' : 'sectors'}
            </span>
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Rows are the primary sectors across the portfolio. Sector values come from official statistics; the average
            assessment and the modelled SOM are AI analysis.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-500">
          Sort
          <select
            data-testid="sector-heatmap-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="rounded-lg border border-[rgba(12,74,110,0.12)] bg-white px-2 py-1 text-xs text-gray-700"
          >
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      </div>

      {portfolio.data?.truncated && (
        <p className="mb-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="sector-heatmap-truncated">
          This portfolio is larger than one view can read, so the table counts only the first part of it.
        </p>
      )}

      {rows.length === 0 ? (
        <p className="rounded-md bg-sky-50 px-3 py-6 text-center text-sm text-gray-600" data-testid="sector-heatmap-empty">
          None of the {model.total} {model.total === 1 ? 'invention' : 'inventions'} in this portfolio has a sector yet. Sectors
          appear here once an invention has been categorised.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-left text-sm" data-testid="sector-heatmap-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-gray-500">
                <th className="px-2 py-2 font-semibold">Sector</th>
                <th className="px-2 py-2 font-semibold">Inventions</th>
                <th className="px-2 py-2 font-semibold">Avg assessment</th>
                <th className="px-2 py-2 font-semibold">Sector value · AU</th>
                <th className="px-2 py-2 font-semibold">Sector value · World</th>
                <th className="px-2 py-2 font-semibold">Σ modelled SOM (midpoint)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const invStep = scaleStep(r.inventions, minInv, maxInv)
                const som = somText(r.som)
                return (
                  <tr key={r.section} data-testid={`heat-row-${r.section}`} className="border-t border-[rgba(12,74,110,0.1)]">
                    <th scope="row" className="px-2 py-2 font-semibold text-iflow-900">
                      <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: sectorColor(r.section) }} aria-hidden />
                      {r.section} {r.name}
                    </th>
                    <td
                      data-testid={`heat-inv-${r.section}`}
                      data-step={invStep}
                      title={`${r.inventions} ${r.inventions === 1 ? 'invention has' : 'inventions have'} this as the primary sector`}
                      className="px-2 py-2 text-center font-semibold"
                      style={{ background: INVENTION_FILL[invStep], color: invStep === 4 ? '#fff' : TEXT }}
                    >
                      {r.inventions}
                    </td>
                    {r.avgAssessment === null ? (
                      <td data-testid={`heat-assess-${r.section}`} title="None of these inventions has an assessment score yet" className="px-2 py-2 text-center text-gray-400">—</td>
                    ) : (
                      <td
                        data-testid={`heat-assess-${r.section}`}
                        data-step={assessmentStep(r.avgAssessment)}
                        title={`Average of ${r.assessedCount} assessed ${r.assessedCount === 1 ? 'invention' : 'inventions'} of ${r.inventions}`}
                        className="px-2 py-2 text-center font-semibold"
                        style={{ background: ASSESSMENT_FILL[assessmentStep(r.avgAssessment)], color: TEXT }}
                      >
                        {r.avgAssessment.toFixed(1)}
                      </td>
                    )}
                    <td data-testid={`heat-au-${r.section}`} className="px-2 py-2 text-gray-700">{refText(r.au)}</td>
                    <td data-testid={`heat-world-${r.section}`} className="px-2 py-2 text-gray-700">{refText(r.world)}</td>
                    <td
                      data-testid={`heat-som-${r.section}`}
                      title={som ? `Adds the SOM midpoint of ${r.somCount} of ${r.inventions} inventions` : 'No invention here has a readable market range'}
                      className="px-2 py-2 font-semibold text-iflow-900"
                    >
                      {som ?? <span className="font-normal text-gray-400">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1" data-testid="sector-heatmap-legend">
          <Swatches fills={INVENTION_FILL} low="fewer inventions" high="more" />
          <Swatches fills={ASSESSMENT_FILL} low="assessment 0" high="10" />
        </div>
      )}

      <div className="mt-3 space-y-1 border-t border-[rgba(12,74,110,0.1)] pt-3 text-[11px] leading-relaxed text-gray-500" data-testid="sector-heatmap-footer">
        <p data-testid="sector-heatmap-footer-facts">
          <span className="font-semibold text-gray-600">Facts:</span>{' '}
          {refs.length > 0
            ? `sector values are official statistics (${sources.join(' and ')}, ${Math.min(...years)}${Math.max(...years) !== Math.min(...years) ? `–${Math.max(...years)}` : ''}), in the unit each source publishes, refreshed ${refreshed ?? 'recently'}; `
            : 'no official sector value is loaded for these sectors yet; '}
          the inventions column counts each invention under its primary sector.
        </p>
        <p data-testid="sector-heatmap-footer-ai">
          <span className="font-semibold text-gray-600">AI analysis:</span>{' '}
          average assessment and modelled SOM are AI-generated estimates{aiWhen ? `, ${aiWhen}` : ', each dated on its invention’s Market tab'}, not investment advice.
          Σ SOM adds the midpoint of the range for {somInventions} {somInventions === 1 ? 'invention' : 'inventions'} the fund may read in full, and
          never adds amounts in different currencies together.
        </p>
        {model.unclassified > 0 && (
          <p data-testid="sector-heatmap-unclassified">
            {model.unclassified} {model.unclassified === 1 ? 'invention has' : 'inventions have'} no primary sector yet and {model.unclassified === 1 ? 'is' : 'are'} not in the table.
          </p>
        )}
      </div>
    </section>
  )
}
